/* eslint-disable no-nested-ternary */
import React, { ReactElement } from 'react'
import classnames from 'classnames/bind'
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback'
import * as ListingUtils from '../utils'
import SimpleMap from '../../layouts/simpleMap'
import styles from './index.scss'
import { AutomationTarget } from '../../../types'

const cx = classnames.bind(styles)

export const LISTING_INFORMATION_PHOTO_WIDTH = 955 // was 750

export const TABS = {
  NONE: null,
  PHOTOS: 0,
  MAP: 1,
  STREET: 2,
}

interface VerifiedImageProps extends AutomationTarget {
  url: string
  featureFlags: Record<string, boolean>
  defaultPhotoConfigKey?: string
  className?: string
  alt?: string
  onClick?: () => void
}

export const VerifiedImage = ({
  url,
  featureFlags,
  defaultPhotoConfigKey,
  ...restProps
}: VerifiedImageProps) => {
  const { formatListingImgUrl } = ListingUtils.ListingFormatters
  const mainImage = useBackgroundImageWithFallback(
    url,
    featureFlags['fe.resi.enabled-photo-enhancement'],
    undefined,
    undefined,
    defaultPhotoConfigKey
  ) as string

  return (
    <img
      src={
        formatListingImgUrl(`${mainImage}`, {
          width: LISTING_INFORMATION_PHOTO_WIDTH,
        }) as string
      }
      {...restProps}
    />
  )
}

interface BackgroundImageProps {
  url?: string
  featureFlags: Record<string, boolean>
  className?: string
  onClick?: () => void
  defaultPhotoConfigKey?: string
}

export const BackgroundImage = ({
  url,
  featureFlags,
  className,
  defaultPhotoConfigKey,
  ...restProps
}: BackgroundImageProps) => {
  const mainImage = useBackgroundImageWithFallback(
    url,
    featureFlags['fe.resi.enabled-photo-enhancement'],
    undefined,
    undefined,
    defaultPhotoConfigKey
  ) as string
  return (
    <div
      className={cx('background-image', className)}
      style={{ backgroundImage: `url(${mainImage})` }}
      {...restProps}
    />
  )
}

interface SecondaryItemProps {
  index: number
  renderer: () => JSX.Element
  className?: string
}

export const SecondaryItem = ({
  index,
  renderer,
  className,
}: SecondaryItemProps) => (
  <div
    key={index}
    className={cx('secondary-photo', `index-${index}`, 'priority', className)}
  >
    {renderer()}
  </div>
)

interface MapItemProps {
  defaultView?: ReactElement
  index: number
  coords: { lat: number; lng: number } | undefined
  setActiveTab: (tab: number) => void
  showStreetView?: boolean
}

export const MapItem = ({
  defaultView,
  index,
  coords,
  setActiveTab,
  showStreetView = false,
}: MapItemProps) => (
  <SecondaryItem
    index={index}
    renderer={() => (
      <SimpleMap
        defaultView={defaultView}
        coords={coords}
        createMapOptions={() => ({
          mapTypeControl: false,
        })}
        onMapClick={() => {
          setActiveTab(showStreetView ? TABS.STREET : TABS.MAP)
        }}
        showStreetView={showStreetView}
      />
    )}
  />
)
